/**
 * Script Editor SIDECAR stores — comments + per-dialogue sound edits.
 *
 * Both are anchored to script ELEMENTS (the structured Final-Draft
 * elements in `StudioScene.elements[]`). The frozen `StudioStore` has no
 * field for either yet, so they persist to their own localStorage keys
 * here. INTEGRATOR NOTE: fold these into StudioStore (e.g.
 * `scriptComments[]` + `lineSoundByElementId`) at merge; the public API
 * below (list / add / update / remove / subscribe) is the contract.
 *
 * Koko can comment too: dispatch a `koko:script-comment` CustomEvent
 * (detail: { sceneId, elementId, text }) and the editor adds it as an
 * agent-authored thread.
 */

const COMMENTS_KEY = "koko_studio_script_comments";
const SOUNDS_KEY = "koko_studio_script_line_sounds";

export type CommentAuthor = "you" | "koko";

export interface ScriptComment {
  id: string;
  sceneId: string;
  /** Anchor element id (StudioScene element). */
  elementId: string;
  /** Snapshot of the anchored text when the comment was made. */
  quote: string;
  text: string;
  author: CommentAuthor;
  resolved: boolean;
  createdAt: number;
}

export type Delivery = "neutral" | "whisper" | "shout" | "warm";

export interface LineSound {
  delivery: Delivery;
  /** Free-text tone note (e.g. "bitter", "playful"). */
  tone?: string;
  /** Playback speed multiplier (1 = normal). */
  speed?: number;
}

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined" || !window.localStorage) return fallback;
  const raw = window.localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota — ignore.
  }
}

// --------------------------------------------------------------------
// Comments
// --------------------------------------------------------------------

export class ScriptCommentStore {
  private comments: ScriptComment[];
  private readonly listeners = new Set<() => void>();

  constructor() {
    this.comments = readJson<ScriptComment[]>(COMMENTS_KEY, []).filter(
      (c) => c && typeof c.id === "string",
    );
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  list(): readonly ScriptComment[] {
    return this.comments;
  }

  listForScene(sceneId: string): ScriptComment[] {
    return this.comments.filter((c) => c.sceneId === sceneId);
  }

  listForElement(elementId: string): ScriptComment[] {
    return this.comments.filter((c) => c.elementId === elementId);
  }

  add(input: {
    sceneId: string;
    elementId: string;
    quote: string;
    text: string;
    author?: CommentAuthor;
  }): ScriptComment {
    const comment: ScriptComment = {
      id: genId("cm"),
      sceneId: input.sceneId,
      elementId: input.elementId,
      quote: input.quote,
      text: input.text.trim(),
      author: input.author ?? "you",
      resolved: false,
      createdAt: Date.now(),
    };
    this.comments = [...this.comments, comment];
    this.flush();
    return comment;
  }

  setResolved(id: string, resolved: boolean): boolean {
    const idx = this.comments.findIndex((c) => c.id === id);
    if (idx === -1) return false;
    const next = [...this.comments];
    next[idx] = { ...next[idx], resolved };
    this.comments = next;
    this.flush();
    return true;
  }

  remove(id: string): boolean {
    if (!this.comments.some((c) => c.id === id)) return false;
    this.comments = this.comments.filter((c) => c.id !== id);
    this.flush();
    return true;
  }

  private flush(): void {
    writeJson(COMMENTS_KEY, this.comments);
    this.listeners.forEach((l) => {
      try {
        l();
      } catch {
        // ignore
      }
    });
  }
}

// --------------------------------------------------------------------
// Per-line sound
// --------------------------------------------------------------------

export class LineSoundStore {
  private byElement: Record<string, LineSound>;
  private readonly listeners = new Set<() => void>();

  constructor() {
    this.byElement = readJson<Record<string, LineSound>>(SOUNDS_KEY, {});
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get(elementId: string): LineSound | null {
    return this.byElement[elementId] ?? null;
  }

  set(elementId: string, sound: LineSound | null): void {
    const next = { ...this.byElement };
    if (sound === null) delete next[elementId];
    else next[elementId] = sound;
    this.byElement = next;
    writeJson(SOUNDS_KEY, this.byElement);
    this.listeners.forEach((l) => {
      try {
        l();
      } catch {
        // ignore
      }
    });
  }
}

export const DEFAULT_LINE_SOUND: LineSound = { delivery: "neutral", speed: 1 };
